/**
 * LoginHistoryPage - Display partner login history and security events
 */

import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  useTheme,
  useMediaQuery
} from '@mui/material';
import AuditLogger, { AuditLogEntry } from '../../core/auth/AuditLogger';
import SecurityIcon from '@mui/icons-material/Security';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';

export const LoginHistoryPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [loginHistory, setLoginHistory] = useState<AuditLogEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedEntry, setSelectedEntry] = useState<AuditLogEntry | null>(null);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    fetchLoginHistory();
  }, []);

  const fetchLoginHistory = async () => {
    try {
      setIsLoading(true);
      const history = await AuditLogger.getLoginHistory(20);
      setLoginHistory(history);
      setError(null);
    } catch (err: any) {
      setError('Failed to fetch login history');
      console.error('Error fetching login history:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString?: string): string => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return dateString;
    }
  };

  const getStatusIcon = (status: 'success' | 'failure') => {
    return status === 'success' ? (
      <CheckCircleIcon sx={{ color: '#10B981' }} />
    ) : (
      <ErrorIcon sx={{ color: '#EF4444' }} />
    );
  };

  const getActionLabel = (action: string): string => {
    const labels: Record<string, string> = {
      'login': 'Login',
      'logout': 'Logout',
      'otp_send': 'OTP Send',
      'otp_verify': 'OTP Verify',
      'register': 'Registration',
      'token_refresh': 'Token Refresh'
    };
    return labels[action] || action;
  };

  const handleViewDetails = (entry: AuditLogEntry) => {
    setSelectedEntry(entry);
    setShowDetails(true);
  };

  const handleCloseDetails = () => {
    setShowDetails(false);
    setSelectedEntry(null);
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <SecurityIcon sx={{ fontSize: 32, color: '#14B8A6' }} />
          <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
            Login History
          </Typography>
        </Box>
        <Typography variant="body2" sx={{ color: '#666' }}>
          View your login activity and security events
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
          <Button size="small" onClick={fetchLoginHistory} sx={{ ml: 2 }}>
            Retry
          </Button>
        </Alert>
      )}

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : loginHistory.length === 0 ? (
        <Card sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" sx={{ color: '#666' }}>
            No login history available
          </Typography>
        </Card>
      ) : (
        <TableContainer component={Card}>
          <Table sx={{ minWidth: isMobile ? 300 : 700 }}>
            <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Action</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Date & Time</TableCell>
                {!isMobile && <TableCell sx={{ fontWeight: 'bold' }}>Details</TableCell>}
                <TableCell sx={{ fontWeight: 'bold', textAlign: 'right' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loginHistory.map((entry, index) => (
                <TableRow key={index} sx={{ '&:hover': { backgroundColor: '#f9f9f9' } }}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {getStatusIcon(entry.status)}
                      <Chip
                        label={entry.status === 'success' ? 'Success' : 'Failed'}
                        size="small"
                        color={entry.status === 'success' ? 'success' : 'error'}
                        variant="outlined"
                      />
                    </Box>
                  </TableCell>
                  <TableCell>{getActionLabel(entry.action)}</TableCell>
                  <TableCell>{formatDate(entry.details?.timestamp)}</TableCell>
                  {!isMobile && (
                    <TableCell>
                      {entry.details?.email && (
                        <Typography variant="caption" sx={{ color: '#666' }}>
                          {entry.details.email}
                        </Typography>
                      )}
                      {entry.details?.phone && (
                        <Typography variant="caption" sx={{ color: '#666' }}>
                          {entry.details.phone}
                        </Typography>
                      )}
                    </TableCell>
                  )}
                  <TableCell sx={{ textAlign: 'right' }}>
                    <Button
                      size="small"
                      onClick={() => handleViewDetails(entry)}
                      sx={{ color: '#14B8A6' }}
                    >
                      View
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Details Dialog */}
      <Dialog open={showDetails} onClose={handleCloseDetails} maxWidth="sm" fullWidth>
        <DialogTitle>Security Event Details</DialogTitle>
        <DialogContent sx={{ py: 3 }}>
          {selectedEntry && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography variant="caption" sx={{ color: '#666' }}>
                  Action
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                  {getActionLabel(selectedEntry.action)}
                </Typography>
              </Box>

              <Box>
                <Typography variant="caption" sx={{ color: '#666' }}>
                  Status
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                  {getStatusIcon(selectedEntry.status)}
                  <Chip
                    label={selectedEntry.status === 'success' ? 'Success' : 'Failed'}
                    size="small"
                    color={selectedEntry.status === 'success' ? 'success' : 'error'}
                    variant="outlined"
                  />
                </Box>
              </Box>

              <Box>
                <Typography variant="caption" sx={{ color: '#666' }}>
                  Date & Time
                </Typography>
                <Typography variant="body2">
                  {formatDate(selectedEntry.details?.timestamp)}
                </Typography>
              </Box>

              {selectedEntry.details?.email && (
                <Box>
                  <Typography variant="caption" sx={{ color: '#666' }}>
                    Email
                  </Typography>
                  <Typography variant="body2">{selectedEntry.details.email}</Typography>
                </Box>
              )}

              {selectedEntry.details?.phone && (
                <Box>
                  <Typography variant="caption" sx={{ color: '#666' }}>
                    Phone
                  </Typography>
                  <Typography variant="body2">{selectedEntry.details.phone}</Typography>
                </Box>
              )}

              {selectedEntry.details?.reason && (
                <Box>
                  <Typography variant="caption" sx={{ color: '#666' }}>
                    Reason
                  </Typography>
                  <Typography variant="body2">{selectedEntry.details.reason}</Typography>
                </Box>
              )}

              {selectedEntry.details?.userAgent && (
                <Box>
                  <Typography variant="caption" sx={{ color: '#666' }}>
                    User Agent
                  </Typography>
                  <Typography variant="caption" sx={{ wordBreak: 'break-all' }}>
                    {selectedEntry.details.userAgent}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetails}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default LoginHistoryPage;
